const MAX_MONEY_CENTS = 999999999999999999n;

/** 严格读取已保存的两位金额，缺失与非法值保留为未知。 */
export function feeMoneyCents(value: string | null | undefined): bigint | null {
  if (typeof value !== "string" || !/^-?\d{1,16}(?:\.\d{1,2})?$/u.test(value.trim())) return null;
  const text = value.trim();
  const [whole, fraction = ""] = text.replace(/^-/, "").split(".");
  const absolute = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
  return absolute > MAX_MONEY_CENTS ? null : text.startsWith("-") ? -absolute : absolute;
}

/** 将整数分转换为两位金额，全过程不经过浮点数。 */
export function feeMoneyText(cents: bigint): string {
  const absolute = cents < 0n ? -cents : cents;
  return `${cents < 0n ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 将整数分转换为最多两位小数的展示文本，计算和提交仍使用固定两位金额。 */
export function feeMoneyCompactText(cents: bigint): string {
  return feeMoneyText(cents).replace(/\.00$/u, "").replace(/(\.\d)0$/u, "$1");
}

/** 以整数分汇总已保存金额；任一金额未知时不展示部分合计。 */
export function sumFeeAdjustmentAmounts(values: Array<string | null | undefined>): string | null {
  let total = 0n;
  for (const value of values) {
    const cents = feeMoneyCents(value);
    if (cents === null) return null;
    total += cents;
  }
  return feeMoneyText(total);
}

/** 单人加价允许负数扣减利润，拒绝空白、超精度或超范围。 */
export function isValidFeeMarkupAmount(value: string): boolean {
  return /^-?(?:0|[1-9]\d*)(?:\.\d{1,2})?$/u.test(value.trim()) && feeMoneyCents(value) !== null;
}

/** 共用严格金额解析、千分位和符号，展示精度由调用入口选择。 */
function formatFeeAdjustmentAmountWith(
  value: string | null | undefined,
  signed: boolean,
  moneyText: (cents: bigint) => string,
): string {
  const cents = feeMoneyCents(value);
  if (cents === null) return "暂无";
  const [whole, fraction] = moneyText(cents < 0n ? -cents : cents).split(".");
  const amount = `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${fraction ? `.${fraction}` : ""}`;
  return `${cents < 0n ? "−" : signed && cents > 0n ? "+" : ""}¥${amount}`;
}

/** 格式化已有金额及方向，缺值不冒充零。 */
export function formatFeeAdjustmentAmount(value: string | null | undefined, signed = true): string {
  return formatFeeAdjustmentAmountWith(value, signed, feeMoneyText);
}

/** 公共只读详情使用 0.## 展示格式，去掉无意义的末尾零。 */
export function formatFeeAdjustmentCompactAmount(value: string | null | undefined, signed = true): string {
  return formatFeeAdjustmentAmountWith(value, signed, feeMoneyCompactText);
}
